(() => {
  'use strict';
  const VERSION = '7.0.0';
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const safeName = (value) => String(value || 'design').trim().replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'design';
  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const download = (name, content, type) => { const blob = content instanceof Blob ? content : new Blob([content], { type }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href=url; a.download=name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),1500); };

  const init = (root) => {
    if (!root || root.dataset.ufProduction604Ready === 'true') return;
    const sidePanels = {
      front: root.querySelector('[data-uf-personalization-side="front"] [data-uf-designer]'),
      back: root.querySelector('[data-uf-personalization-side="back"] [data-uf-designer]')
    };
    const panel = root.querySelector('[data-uf-production-tools]');
    const designer = sidePanels.front;
    const svg = designer?.querySelector('[data-uf-canvas-svg]');
    const designInput = designer?.querySelector('[data-uf-design-json]');
    if (!designer || !panel || !svg || !designInput) return;
    root.dataset.ufProduction604Ready = 'true';

    const status = panel.querySelector('[data-uf-production-status]');
    const estimateOut = panel.querySelector('[data-uf-production-estimate]');
    const jobInput = panel.querySelector('[data-uf-production-job]');
    const operatorInput = panel.querySelector('[data-uf-production-operator]');
    const machineInput = panel.querySelector('[data-uf-production-machine]');
    const operationInput = panel.querySelector('[data-uf-production-operation]');
    const notesInput = panel.querySelector('[data-uf-production-notes]');
    const productTitle = root.dataset.productTitle || document.querySelector('h1')?.textContent?.trim() || 'Personalized product';
    const productHandle = root.dataset.productHandle || location.pathname.split('/').filter(Boolean).pop() || 'product';

    const setStatus = (message, error=false) => { if(status){status.textContent=message; status.setAttribute('role', error?'alert':'status');} };
    const parseData = (input) => { try{return JSON.parse((input || designInput).value || '{}');}catch(_){return{};} };

    const imageHref = (node) => node?.getAttribute('href') || node?.getAttributeNS('http://www.w3.org/1999/xlink', 'href') || node?.getAttribute('xlink:href') || '';
    const setImageHref = (node, value) => {
      if (!node) return;
      if (!value) {
        node.removeAttribute('href');
        node.removeAttribute('xlink:href');
        return;
      }
      node.setAttribute('href', value);
      node.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', value);
    };
    const urlToDataUrl = async (url) => {
      if (!url) return url;
      if (/^data:/i.test(url) || /^blob:/i.test(url)) return url;
      const absoluteUrl = url.startsWith('//') ? `https:${url}` : url;
      if (!/^https?:/i.test(absoluteUrl)) return url;
      try {
        const response = await fetch(absoluteUrl, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) return url;
        const blob = await response.blob();
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : url);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (_error) {
        return url;
      }
    };
    const qrUrl = (value, size=300) => value
      ? `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=0&data=${encodeURIComponent(value)}`
      : '';

    const cleanSvg = async (side, sideSvg, includeProductPreview=false) => {
      const clone = sideSvg.cloneNode(true);
      const sourceNodes = [sideSvg, ...sideSvg.querySelectorAll('*')];
      const cloneNodes = [clone, ...clone.querySelectorAll('*')];
      const styleProps = [
        'fill','fill-opacity','stroke','stroke-width','stroke-opacity','stroke-dasharray','stroke-linecap','stroke-linejoin',
        'opacity','font-family','font-size','font-style','font-weight','letter-spacing','text-anchor','dominant-baseline','paint-order'
      ];

      // Freeze all live Shopify CSS into the cloned SVG before it is moved into
      // a standalone proof window or downloaded as a production file.
      sourceNodes.forEach((sourceNode, index) => {
        const cloneNode = cloneNodes[index];
        if (!cloneNode || sourceNode.nodeType !== 1) return;
        const computed = getComputedStyle(sourceNode);
        styleProps.forEach((property) => {
          const value = computed.getPropertyValue(property);
          if (value) cloneNode.style.setProperty(property, value);
        });
      });

      clone.removeAttribute('style');
      clone.setAttribute('xmlns','http://www.w3.org/2000/svg');
      clone.setAttribute('xmlns:xlink','http://www.w3.org/1999/xlink');
      clone.querySelectorAll('[data-uf-canvas-selection],[data-uf-guide-x],[data-uf-guide-y],[data-uf-safe-zone],.uf-canvas__selection,.uf-canvas__guide').forEach((n)=>n.remove());
      if (!includeProductPreview) clone.querySelectorAll('[data-uf-product-preview],[data-uf-product-wash],[data-uf-export-role="product-preview"],.uf-product-canvas__fallback').forEach((n)=>n.remove());

      // The live SVG already reflects exactly what the customer sees for this
      // side (text content, artwork, visibility). Just drop any layer that's
      // actually hidden or empty rather than re-deriving state from JSON.
      clone.querySelectorAll('[data-uf-canvas-object]').forEach((group) => {
        const id = group.dataset.ufCanvasObject || '';
        const wasHidden = group.hasAttribute('hidden') || group.style.display === 'none';
        const textNode = group.querySelector('text');
        const imageNode = group.querySelector('image');
        const hasText = Boolean((textNode?.textContent || '').trim());
        const hasImage = Boolean(imageHref(imageNode));
        const hasVector = Boolean(group.querySelector('path,circle,ellipse,polygon,polyline,rect,line'));

        if (wasHidden || (!hasText && !hasImage && !hasVector)) { group.remove(); return; }
        if (id === 'text' && !hasText) { group.remove(); return; }
        if ((id === 'artwork' || id === 'qr') && !hasImage) { group.remove(); return; }

        group.hidden = false;
        group.removeAttribute('hidden');
        group.removeAttribute('aria-hidden');
        group.querySelectorAll('[hidden]').forEach((node) => { node.hidden = false; node.removeAttribute('hidden'); });
        group.querySelectorAll('[display="none"]').forEach((node) => node.removeAttribute('display'));
        group.style.removeProperty('display');
        group.style.removeProperty('visibility');
        group.style.removeProperty('cursor');
        group.style.removeProperty('pointer-events');
        group.removeAttribute('tabindex');
        group.removeAttribute('aria-label');
      });

      // Embed uploaded artwork and remotely generated QR images whenever CORS permits.
      // Uploaded artwork is already a data URL; this also makes QR exports portable.
      for (const image of clone.querySelectorAll('image')) {
        const href = imageHref(image);
        if (href) setImageHref(image, await urlToDataUrl(href));
      }

      const meta=document.createElementNS('http://www.w3.org/2000/svg','metadata');
      meta.textContent=`UNIQ Design Studio production export ${VERSION} ${side}`;
      clone.insertBefore(meta,clone.firstChild);
      return new XMLSerializer().serializeToString(clone);
    };

    const collectSvgs = async (includeProductPreview=false) => {
      const result = { front: '' };
      result.front = await cleanSvg('front', svg, includeProductPreview);
      const backSvg = sidePanels.back?.querySelector('[data-uf-canvas-svg]');
      const backPanelHidden = root.querySelector('[data-uf-back-panel]')?.hidden;
      if (backSvg && backPanelHidden === false) {
        result.back = await cleanSvg('back', backSvg, includeProductPreview);
      }
      return result;
    };

    const visibleLayerCount = () => root.querySelectorAll('[data-uf-canvas-object]:not([hidden])').length;
    const estimate = () => {
      const data=parseData(); const layers=visibleLayerCount();
      const backPanel = root.querySelector('[data-uf-back-panel]');
      const hasBack = Boolean(backPanel && backPanel.hidden === false);
      const qr=JSON.stringify(data.qr||{}).length>40; const art=JSON.stringify(data.artwork||{}).includes('true');
      let minutes=4 + layers*2 + (hasBack?5:0) + (qr?3:0) + (art?4:0);
      const operation=operationInput?.value || 'engrave'; if(operation==='cut')minutes+=6; if(operation==='engrave-cut')minutes+=9;
      minutes=Math.max(5,Math.round(minutes)); if(estimateOut)estimateOut.textContent=`${minutes} min`; return minutes;
    };

    const packageData = async (includeSvg=true) => {
      const frontData=parseData(designInput);
      const backInput = sidePanels.back?.querySelector('[data-uf-design-json]');
      const backPanelHidden = root.querySelector('[data-uf-back-panel]')?.hidden;
      const backData = (backInput && backPanelHidden === false) ? parseData(backInput) : null;
      const svgs=includeSvg?await collectSvgs(true):undefined;
      return {
        schema:'uniq-flair-production-job', version:VERSION, generatedAt:new Date().toISOString(),
        product:{title:productTitle,handle:productHandle,url:location.href.split('?')[0],variant:root.querySelector('input[name="id"]')?.value||''},
        job:{number:jobInput?.value.trim()||'',operator:operatorInput?.value.trim()||'',machine:machineInput?.value||'',operation:operationInput?.value||'engrave',status:'ready-to-review',estimatedMinutes:estimate(),notes:notesInput?.value.trim()||root.querySelector('[name="properties[Design notes]"]')?.value.trim()||''},
        customerDesign:{front:frontData, back:backData},
        production:{coordinateSystem:frontData?.canvas?.coordinateSystem||svg.getAttribute('viewBox')||'600x360',safeZone:frontData?.canvas?.safeZone||{x:58,y:48,width:484,height:264},sides:includeSvg?svgs:undefined}
      };
    };

    const reportHtml = (job, svgs, type) => {
      const title='Production Job Sheet';
      const rows=[['Product',job.product.title],['Job number',job.job.number||'Not assigned'],['Machine',job.job.machine||'Not selected'],['Operation',job.job.operation],['Operator',job.job.operator||'Not assigned'],['Estimated machine time',`${job.job.estimatedMinutes} minutes`],['Generated',new Date(job.generatedAt).toLocaleString()]];
      return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>@page{size:A4;margin:14mm}body{font-family:Arial,sans-serif;color:#111;margin:0}header{border-bottom:3px solid #111;padding-bottom:10px;margin-bottom:18px}h1{font-size:24px;margin:0 0 5px}.sub{color:#555}.grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}.preview{border:1px solid #bbb;border-radius:8px;padding:10px;break-inside:avoid}.preview h2{font-size:16px;margin:0 0 8px}.preview svg{width:100%;height:auto;background:#fff}.meta{width:100%;border-collapse:collapse;margin:18px 0}.meta td{border:1px solid #ccc;padding:7px}.meta td:first-child{font-weight:bold;width:35%}.notes,.approval{border:1px solid #bbb;padding:12px;min-height:70px;margin-top:14px}.line{display:inline-block;border-bottom:1px solid #111;min-width:210px;height:20px;margin:0 12px}.small{font-size:11px;color:#666;margin-top:16px}@media print{button{display:none}}</style></head><body><header><h1>${esc(title)}</h1><div class="sub">UNIQ Design Studio ${VERSION}</div></header><div class="grid"><section class="preview"><h2>Front</h2>${svgs.front||''}</section>${svgs.back?`<section class="preview"><h2>Back</h2>${svgs.back}</section>`:''}</div><table class="meta">${rows.map(([a,b])=>`<tr><td>${esc(a)}</td><td>${esc(b)}</td></tr>`).join('')}</table><section class="notes"><strong>Production notes</strong><p>${esc(job.job.notes||'None')}</p></section><p class="small">Preview colours are representational. Final engraving appearance varies by product and material.</p><script>setTimeout(()=>window.print(),400)<\/script></body></html>`;
    };

    const openReport = async () => { setStatus('Preparing job sheet…'); try{const svgs=await collectSvgs(true); const job=await packageData(false); const win=window.open('','_blank'); if(!win)throw new Error('Pop-up blocked. Allow pop-ups and try again.'); win.document.open(); win.document.write(reportHtml(job,svgs,'job')); win.document.close(); setStatus('Job sheet opened. Choose Print → Save as PDF.');}catch(e){setStatus(e.message||'Unable to create document.',true);} };

    panel.querySelector('[data-uf-export-json]')?.addEventListener('click',async()=>{setStatus('Preparing production JSON…'); try{const job=await packageData(true); download(`${safeName(productTitle)}-production-job.json`,JSON.stringify(job,null,2),'application/json'); setStatus('Production JSON downloaded.');}catch(e){setStatus(e.message,true);}});
    panel.querySelector('[data-uf-export-svg]')?.addEventListener('click',async()=>{setStatus('Preparing SVG files…'); try{const svgs=await collectSvgs(false); const base=safeName(productTitle); download(`${base}-front.svg`,svgs.front,'image/svg+xml'); if(svgs.back){await sleep(150); download(`${base}-back.svg`,svgs.back,'image/svg+xml');} setStatus(svgs.back?'Front and back SVG files downloaded.':'Front SVG file downloaded.');}catch(e){setStatus(e.message,true);}});
    panel.querySelector('[data-uf-print-job]')?.addEventListener('click',openReport);
    panel.querySelector('[data-uf-copy-summary]')?.addEventListener('click',async()=>{try{const job=await packageData(false); await navigator.clipboard.writeText(JSON.stringify(job,null,2)); setStatus('Production summary copied.');}catch(e){setStatus('Clipboard access was unavailable.',true);}});

    // Send the finished design to a merchant-configured webhook (e.g. Zapier or
    // Make.com) the moment a personalized item is added to the cart, so an
    // automation on the other end can email it to production/admin. This is
    // separate from the manual export buttons above and runs silently.
    const webhookUrl = (root.dataset.ufProductionWebhook || '').trim();
    const cartForm = root.querySelector('form[action*="/cart/add"]') || root.querySelector('.uf-personalization-form');
    if (webhookUrl && cartForm) {
      cartForm.addEventListener('submit', (event) => {
        if (cartForm.dataset.ufWebhookSent === 'true') return; // second, real submission -- let it proceed
        event.preventDefault();

        const proceed = () => {
          cartForm.dataset.ufWebhookSent = 'true';
          if (cartForm.requestSubmit) cartForm.requestSubmit();
          else cartForm.submit();
        };

        // Safety net: never let a slow or unreachable webhook block checkout.
        const safety = window.setTimeout(proceed, 2500);

        packageData(true).then((job) => {
          const payload = JSON.stringify({ event: 'uf_item_added_to_cart', sentAt: new Date().toISOString(), job });
          // A plain fetch (no keepalive/sendBeacon) has no ~64KB size cap, unlike
          // the methods that survive page unload -- needed since this payload
          // includes the embedded product photo and can be well over that limit.
          // We hold the actual form submission open (above) specifically so this
          // has time to complete before the page would otherwise navigate away.
          return fetch(webhookUrl, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: payload, mode: 'no-cors' });
        }).catch(() => {}).finally(() => {
          window.clearTimeout(safety);
          proceed();
        });
      });
    }

    [operationInput,machineInput].forEach((el)=>el?.addEventListener('change',estimate));
    root.addEventListener('uf:canvas-change',estimate); root.addEventListener('input',()=>setTimeout(estimate,100));
    root.querySelector('[data-uf-back-toggle]')?.addEventListener('change',()=>setTimeout(estimate,50));
    estimate();
  };
  const boot=()=>document.querySelectorAll('[data-uf-engraving-product]').forEach((root)=>{const panel=root.querySelector('[data-uf-production-tools]');if(panel && new URLSearchParams(location.search).get('uf_production')==='1')panel.hidden=false;init(root);}); document.readyState==='loading'?document.addEventListener('DOMContentLoaded',boot):boot(); document.addEventListener('shopify:section:load',boot);
})();
